import { useState } from 'react';
import { cn } from '@/components/ui/utils';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useTimeLog } from '../hooks/useTimeLog';
import { formatDuration } from '../hooks/useDayTimer';
import { AppTopBar } from './AppTopBar';
import { DetailSkeleton } from './shared/InternSkeletons';
import { InternErrorState } from './shared/InternEmptyState';
import type { TimeLogEntry, TimeDay } from '../hooks/useTimeLog';

export function TimeLogPage() {
  const { user } = useAuth();
  const { userProfile } = usePermissions();
  const { days, loading, error, totals, addManualEntry, editEntry, refresh } = useTimeLog();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const userName = userProfile?.display_name || user?.email?.split('@')[0] || 'there';
  const hasEntries = days.length > 0;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-warm-white)' }}>
      <AppTopBar userName={userName} />

      <main className="mx-auto max-w-[880px] px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <h1
            className="font-serif font-[600] text-[36px] leading-none tracking-[-0.02em]"
            style={{ fontFamily: 'var(--font-serif)', color: 'var(--color-near-black)' }}
          >
            Your Time
          </h1>
          <button
            onClick={() => setShowAddForm((v) => !v)}
            className={cn(
              'px-4 py-2 rounded-[4px] text-[14px] font-[500] transition-all duration-200',
              'bg-[var(--color-gold)] text-[var(--color-near-black)]',
              'hover:bg-[var(--color-gold-light)] active:scale-95'
            )}
          >
            Add entry
          </button>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 gap-6 mb-10">
          <TotalCard label="Today" value={formatDuration(totals.todayMin)} />
          <TotalCard label="This week" value={formatDuration(totals.weekMin)} />
        </div>

        {/* Add entry form */}
        {showAddForm && (
          <TimeEntryForm
            onSave={async (data) => {
              const { error } = await addManualEntry(data);
              if (!error) setShowAddForm(false);
            }}
            onCancel={() => setShowAddForm(false)}
          />
        )}

        {/* Days worked */}
        {loading && <DetailSkeleton />}
        {error && <InternErrorState onRetry={refresh} />}

        {!loading && !error && (
          !hasEntries ? (
            <p className="text-[15px] text-[var(--color-mid-gray)] text-center py-12">
              No time logged yet — hit <strong>Start</strong> in the top bar.
            </p>
          ) : (
            <div className="space-y-8">
              {days.map((day) => (
                <DayGroup
                  key={day.date}
                  day={day}
                  editingId={editingId}
                  onEdit={setEditingId}
                  onSaveEdit={async (id, data) => {
                    await editEntry(id, data);
                    setEditingId(null);
                  }}
                  onCancelEdit={() => setEditingId(null)}
                />
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
}

// Sub-components

function DayGroup({
  day,
  editingId,
  onEdit,
  onSaveEdit,
  onCancelEdit,
}: {
  day: TimeDay;
  editingId: string | null;
  onEdit: (id: string) => void;
  onSaveEdit: (id: string, data: FormData) => Promise<void>;
  onCancelEdit: () => void;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3 pb-2 border-b border-[var(--border)]">
        <h2 className="text-[16px] font-[600]" style={{ color: 'var(--color-near-black)' }}>
          {formatDayHeading(day.date)}
        </h2>
        <span className="text-[15px] font-[600] text-[var(--color-forest)]">
          {formatDuration(day.totalMin)}
        </span>
      </div>
      <div className="space-y-2">
        {day.entries.map((entry) =>
          editingId === entry.id ? (
            <TimeEntryForm
              key={entry.id}
              initialValues={entry}
              onSave={(data) => onSaveEdit(entry.id, data)}
              onCancel={onCancelEdit}
            />
          ) : (
            <TimeEntryRow key={entry.id} entry={entry} onEdit={() => onEdit(entry.id)} />
          )
        )}
      </div>
    </div>
  );
}

function formatDayHeading(date: string): string {
  return new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function TotalCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[4px] border border-[var(--border)] bg-white p-5">
      <p className="text-[13px] font-[500] text-[var(--color-mid-gray)] uppercase tracking-wide mb-1">{label}</p>
      <p
        className="font-serif font-[600] text-[36px] leading-none"
        style={{ fontFamily: 'var(--font-serif)', color: 'var(--color-near-black)' }}
      >
        {value || '0m'}
      </p>
    </div>
  );
}

function TimeEntryRow({ entry, onEdit }: { entry: TimeLogEntry; onEdit: () => void }) {
  const start = new Date(entry.started_at);
  const end = entry.ended_at ? new Date(entry.ended_at) : null;

  return (
    <div className="flex items-center justify-between gap-4 rounded-[4px] border border-[var(--border)] bg-white px-5 py-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-[14px] font-[600] text-[var(--color-near-black)]">
            {formatDateShort(start)}
          </span>
          <span className="text-[14px] text-[var(--color-charcoal)]">
            {formatTimeShort(start)} – {end ? formatTimeShort(end) : '…'}
          </span>
          {entry.duration_min != null && (
            <span className="text-[14px] font-[600] text-[var(--color-forest)]">
              {formatDuration(entry.duration_min)}
            </span>
          )}
        </div>
        {entry.task_title && (
          <p className="text-[13px] text-[var(--color-mid-gray)] mt-0.5 truncate">{entry.task_title}</p>
        )}
        {entry.note && (
          <p className="text-[13px] text-[var(--color-mid-gray)] mt-0.5 truncate">{entry.note}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {(entry.edited || entry.source === 'manual') && (
          <span className="text-[11px] text-[var(--color-mid-gray)] uppercase tracking-wide">
            {entry.source === 'manual' ? 'manual' : 'edited'}
          </span>
        )}
        <button
          onClick={onEdit}
          className="text-[13px] text-[var(--color-forest)] hover:underline transition-colors"
        >
          Edit
        </button>
      </div>
    </div>
  );
}

interface FormData {
  started_at: string;
  ended_at: string;
  duration_min: number;
  task_id?: string | null;
  note?: string | null;
}

function TimeEntryForm({
  initialValues,
  onSave,
  onCancel,
}: {
  initialValues?: TimeLogEntry;
  onSave: (data: FormData) => Promise<void>;
  onCancel: () => void;
}) {
  const [startTime, setStartTime] = useState(
    initialValues?.started_at
      ? toLocalDatetimeInput(new Date(initialValues.started_at))
      : toLocalDatetimeInput(new Date())
  );
  const [endTime, setEndTime] = useState(
    initialValues?.ended_at
      ? toLocalDatetimeInput(new Date(initialValues.ended_at))
      : toLocalDatetimeInput(new Date())
  );
  const [note, setNote] = useState(initialValues?.note ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const startMs = new Date(startTime).getTime();
    const endMs = new Date(endTime).getTime();
    const durationMin = Math.max(1, Math.round((endMs - startMs) / 60000));
    await onSave({
      started_at: new Date(startTime).toISOString(),
      ended_at: new Date(endTime).toISOString(),
      duration_min: durationMin,
      note: note || null,
    });
    setSaving(false);
  };

  return (
    <div className="rounded-[4px] border border-[var(--color-forest)]/30 bg-white p-5 space-y-4 mb-2">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-[13px] font-[500] text-[var(--color-mid-gray)] mb-1">Start</label>
          <input
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="w-full rounded-[4px] border border-[var(--border)] px-3 py-2 text-[14px] text-[var(--color-near-black)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--color-forest)]"
          />
        </div>
        <div>
          <label className="block text-[13px] font-[500] text-[var(--color-mid-gray)] mb-1">End</label>
          <input
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="w-full rounded-[4px] border border-[var(--border)] px-3 py-2 text-[14px] text-[var(--color-near-black)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--color-forest)]"
          />
        </div>
      </div>
      <div>
        <label className="block text-[13px] font-[500] text-[var(--color-mid-gray)] mb-1">Note (optional)</label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What were you working on?"
          className="w-full rounded-[4px] border border-[var(--border)] px-3 py-2 text-[14px] text-[var(--color-near-black)] bg-white placeholder:text-[var(--color-mid-gray)] focus:outline-none focus:ring-2 focus:ring-[var(--color-forest)]"
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 rounded-[4px] text-[14px] font-[500] bg-[var(--color-forest)] text-[var(--color-warm-white)] hover:bg-[var(--color-forest-light)] disabled:opacity-50 transition-all active:scale-95"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-[4px] text-[14px] font-[500] border border-[var(--border)] text-[var(--color-charcoal)] hover:bg-[var(--muted)] transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Helpers
function formatDateShort(d: Date) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function formatTimeShort(d: Date) {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function toLocalDatetimeInput(d: Date): string {
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}
